import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ChooseFlightData } from 'src/app/flight/models/ChooseFlightData';
import { FlightsFormData } from 'src/app/flight/models/FlightsFormData';
import { RequestStatus } from 'src/app/models/RequestStatus';
import { Direction } from 'src/app/flight/models/Direction';
import { getItemFromSessionStorage, CHOOSE_FLIGHT_DATA, setItemInSessionStorage } from 'src/app/utils/SessionStorageUtil';
import { OTAAirScheduleResponse, OriginDestinationOption } from '../models/OTAAirScheduleResponse';

function InboundValidatorRequired(flightsFormData: FlightsFormData): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (flightsFormData.isRoundTrip() && !value) {
      return { required: true };
    }

    return null;
  }
}

@Component({
    selector: 'app-choose-flight',
    templateUrl: './choose-flight.component.html',
    styleUrls: ['./choose-flight.component.scss'],
    standalone: false
})
export class ChooseFlightComponent implements OnInit {

  Direction = Direction;

  @Input() direction: Direction;
  @Input() flightsFormData: FlightsFormData;
  @Input() flights: OTAAirScheduleResponse;
  @Input() form: FormGroup;
  @Input() proceeded: boolean;

  @ViewChild('panelArrow') panelArrow: ElementRef;
  @ViewChild('panelBody') panelBody: ElementRef;

  filterDirectOnly = false;

  get filteredFlights(): OriginDestinationOption[] {
    if (!this.flights?.destinationOptionList) return [];
    if (!this.filterDirectOnly) return this.flights.destinationOptionList;
    return this.flights.destinationOptionList.filter(option => option.flightSegments.length === 1);
  }

  onDirectFlightsToggle(): void {
    // La lista se actualiza automáticamente vía filteredFlights getter
    // La selección del usuario se mantiene en el form (no se limpia)
  }

  ngOnInit(): void {
    // Ordenar los vuelos por departureDateTime antes de cualquier otra lógica
    this.sortFlightsByDepartureDateTime();

    const validatorsByDirection = new Map<Direction, ValidatorFn[]>([
      [Direction.OUTBOUND, [Validators.required]],
      [Direction.INBOUND, [InboundValidatorRequired(this.flightsFormData)]]
    ]);

    const validators: ValidatorFn[] = validatorsByDirection.get(this.direction) || [];
    this.form.addControl(this.direction.name, new FormControl('', validators));
    this.fillFormWithDataFromStorage();

    const reset = this.form.reset.bind(this.form);
    this.form.reset = () => {
      reset();
      this.openCollapseFlights();
    }
  }

  sortFlightsByDepartureDateTime(): void {
    if (this.flights?.destinationOptionList) {
      this.flights.destinationOptionList.sort((a: OriginDestinationOption, b: OriginDestinationOption) => {
        const dateA = a.flightSegments.length > 0 
          ? new Date(a.flightSegments[0].departureDateTime).getTime() 
          : Infinity; 
        const dateB = b.flightSegments.length > 0 
          ? new Date(b.flightSegments[0].departureDateTime).getTime() 
          : Infinity; 
  
        return dateA - dateB;
      });
    }
  }

  selectCheckbox(event: any, flight: OriginDestinationOption): void {
    if (event.checked) {
      this.form.get(this.direction.name)?.setValue(flight);
      this.closeCollapseFlights();
    } else {
      this.form.get(this.direction.name)?.setValue(null);
    }
    setItemInSessionStorage(CHOOSE_FLIGHT_DATA, this.form.getRawValue());
  }

  isCheckboxDisabled(option: OriginDestinationOption): boolean {
    return option.flightSegments.some(segment => segment.blocked);
  }

  checked(flight: OriginDestinationOption): boolean {
    return JSON.stringify(flight) === JSON.stringify(this.form.get(this.direction.name)?.value);
  }

  toggleCollapseFlights(): void {
    this.panelBody?.nativeElement.classList.toggle('closed');
    this.panelArrow?.nativeElement.classList.toggle('closed');
  }

  closeCollapseFlights(): void {
    this.panelBody?.nativeElement.classList.add('closed');
    this.panelArrow?.nativeElement.classList.add('closed');
  }

  openCollapseFlights(): void {
    this.panelBody?.nativeElement.classList.remove('closed');
    this.panelArrow?.nativeElement.classList.remove('closed');
  }

  toggleItinerary(position: number): void {
    const id = `card-itinerary-${this.direction.name}-${position}`;
    const cardItinerary = document.getElementById(id);

    cardItinerary?.classList.toggle('card-itinerary');
    cardItinerary?.classList.toggle('card-itinerary-opened');
  }

  fillFormWithDataFromStorage(): void {
    const chooseFlightData = getItemFromSessionStorage<ChooseFlightData>(CHOOSE_FLIGHT_DATA);

    if (chooseFlightData) {
      this.form.get(this.direction.name)?.setValue(chooseFlightData[this.direction.name as keyof ChooseFlightData]);
    }
  }
}
